import { PROGRAM_PROFILES } from "../data/programProfiles";
import { PROGRAM_NAME_MAP } from "../data/programMappings";
import { getSecondaryPersonality, rankPersonalityProfiles } from "./personalityBlendEngine";
import { normalizeScores } from "./fitEngine";

function getLevel(score = 50) {
  if (score >= 70) return "high";
  if (score >= 40) return "moderate";
  return "low";
}

export function getPrimaryPersonality(normalized) {
  const profile = {
    inclusion: getLevel(normalized?.inclusion),
    control: getLevel(normalized?.control),
    affection: getLevel(normalized?.affection),
    thinking: getLevel(normalized?.thinking),
    flexibility: getLevel(normalized?.flexibility)
  };

  const {
    inclusion,
    control,
    affection,
    thinking,
    flexibility
  } = profile;

  if (thinking === "high" && control === "high") {
    return "analytical_thinker";
  }

  if (inclusion === "high" && affection === "high") {
    return "versatile_collaborator";
  }

  if (control === "high" && flexibility === "low") {
    return "strategic_planner";
  }

  if (flexibility === "high" && thinking !== "low") {
    return "creative_explorer";
  }

  if (
    inclusion === "moderate" &&
    control === "moderate" &&
    affection === "moderate"
  ) {
    return "practical_operator";
  }

  return "independent_problem_solver";
}

/**
 * ✅ PERSONALITY SUMMARY (NEW MODEL)
 */
export function getPersonalitySummary(scores, scoreRanges) {
  const normalized =
    normalizeScores(scores, scoreRanges);

  const personalityRanking =
    rankPersonalityProfiles(
      normalized
    );

  const type =
    getPrimaryPersonality(normalized);

  const primaryMatch =
    personalityRanking.find(
      candidate => candidate.type === type
    );

  const blend =
    getSecondaryPersonality(
      type,
      normalized
    );

  const secondaryMatch =
    blend
      ? personalityRanking.find(
          candidate =>
            candidate.type === blend.secondary
        )
      : null;

  const supportedBlend =
    blend &&
    primaryMatch &&
    secondaryMatch &&
    primaryMatch.similarity >=
      secondaryMatch.similarity
      ? blend
      : null;

  return {
    type,

    primarySimilarity:
      Math.round(
        primaryMatch?.similarity || 0
      ),

    blend: supportedBlend,

    personalityRanking:
      personalityRanking.map(
        candidate => ({
          type: candidate.type,

          similarity:
            Math.round(
              candidate.similarity
            )
        })
      ),

    insights:
      generateInsights(normalized),
    
    learningPreferences:
      generateLearningPreferences(normalized)  
  };
}

/**
 * ✅ INSIGHTS ENGINE (NEW — DIMENSION-BASED)
 */
export function generateInsights(scores) {
  const insights = [];

  const {
    inclusion,
    control,
    affection,
    thinking,
    flexibility
  } = scores;

  // --- INCLUSION ---
  if (inclusion >= 70) {
    insights.push("You actively engage with others and thrive in social environments.");
  } else if (inclusion <= 30) {
    insights.push("You prefer working independently and value personal space.");
  } else {
    insights.push("You balance collaboration with independence depending on the situation.");
  }

  // --- CONTROL ---
  if (control >= 70) {
    insights.push("You naturally take initiative and prefer leading or structuring tasks.");
  } else if (control <= 30) {
    insights.push("You prefer guidance and clear direction when approaching tasks.");
  } else {
    insights.push("You can both lead and follow depending on what the situation requires.");
  }

  // --- AFFECTION ---
  if (affection >= 70) {
    insights.push("You value strong relationships and emotional connection with others.");
  } else if (affection <= 30) {
    insights.push("You maintain emotional independence and focus more on tasks than relationships.");
  } else {
    insights.push("You balance emotional connection with personal boundaries.");
  }

  // --- THINKING ---
  if (thinking >= 70) {
    insights.push("You rely heavily on logic, analysis, and critical thinking when making decisions.");
  } else if (thinking <= 30) {
    insights.push("You prefer intuitive or experience-based decision making.");
  } else {
    insights.push("You combine logical reasoning with intuition when solving problems.");
  }

  // --- FLEXIBILITY ---
  if (flexibility >= 70) {
    insights.push("You adapt quickly to change and are comfortable exploring new approaches.");
  } else if (flexibility <= 30) {
    insights.push("You prefer stability, structure, and predictable environments.");
  } else {
    insights.push("You balance structure with adaptability depending on context.");
  }

  return insights;
}

export function generateLearningPreferences(scores) {
  const preferences = [];

  const {
    inclusion,
    control,
    thinking,
    flexibility
  } = scores;

  if (inclusion >= 70) {
    preferences.push(
      "Discussion-based and collaborative learning activities"
    );
  } else if (inclusion <= 30) {
    preferences.push(
      "Independent learning with space for focused work"
    );
  } else {
    preferences.push(
      "A balance of independent and collaborative learning"
    );
  }

  if (control >= 70) {
    preferences.push(
      "Structured coursework with clear goals and opportunities to take responsibility"
    );
  } else if (control <= 30) {
    preferences.push(
      "Clear guidance, examples, and step-by-step direction"
    );
  } else {
    preferences.push(
      "Clear objectives with room to choose how tasks are completed"
    );
  }

  if (thinking >= 70) {
    preferences.push(
      "Analytical tasks involving evidence, logic, and careful reasoning"
    );
  } else if (thinking <= 30) {
    preferences.push(
      "Practical demonstrations and experience-based learning"
    );
  } else {
    preferences.push(
      "A combination of logical explanation and practical application"
    );
  }

  if (flexibility >= 70) {
    preferences.push(
      "Varied activities that allow experimentation and new approaches"
    );
  } else if (flexibility <= 30) {
    preferences.push(
      "Consistent routines and predictable assessment structures"
    );
  } else {
    preferences.push(
      "Stable expectations with some variety in learning activities"
    );
  }

  return preferences;
}

export function generateProgramExplanation(
  fit,
  personality,
  level
) {
  if (!fit?.program || !personality?.type) {
    return "Program aligned with your personality profile.";
  }

  const profile =
    PROGRAM_PROFILES[
      PROGRAM_NAME_MAP[fit.program] || fit.program
    ];

  if (!profile?.fitExplanations) {
    return "Program aligned with your personality profile.";
  }

  const levelExplanations =
    profile.fitExplanations[level];

  if (!levelExplanations) {
    return "Program aligned with your personality profile.";
  }

  // ✅ OLD ARCHITECTURE SUPPORT
  if (typeof levelExplanations === "string") {
    return levelExplanations;
  }

  // ✅ NEW ARCHITECTURE SUPPORT
  return (
    levelExplanations[personality.type] ||
    Object.values(levelExplanations)[0] ||
    "Program aligned with your personality profile."
  );
}
